import atherton from "@/assets/peninsula/atherton.jpg";
import belmont from "@/assets/peninsula/belmont.jpg";
import brisbane from "@/assets/peninsula/brisbane.jpg";
import burlingame from "@/assets/peninsula/burlingame.jpg";
import halfMoonBay from "@/assets/peninsula/half-moon-bay.jpg";
import hillsborough from "@/assets/peninsula/hillsborough.jpg";
import menloPark from "@/assets/peninsula/menlo-park.jpg";
import millbrae from "@/assets/peninsula/millbrae.jpg";
import redwoodCity from "@/assets/peninsula/redwood-city.jpg";
import sanBruno from "@/assets/peninsula/san-bruno.jpg";
import sanCarlos from "@/assets/peninsula/san-carlos.jpg";
import sanMateo from "@/assets/peninsula/san-mateo.jpg";
import woodside from "@/assets/peninsula/woodside.jpg";

interface Slide {
  src: string;
  city: string;
  caption: string;
  alt: string;
}

const slides: Slide[] = [
  { src: atherton, city: "Atherton", caption: "Estate Living", alt: "Atherton California luxury estate at twilight" },
  { src: belmont, city: "Belmont", caption: "Tree-lined Streets", alt: "Tree-lined residential street in Belmont California at golden hour" },
  { src: brisbane, city: "Brisbane", caption: "Bay Views", alt: "Brisbane California hillside neighborhood overlooking the San Francisco Bay" },
  { src: burlingame, city: "Burlingame", caption: "Burlingame Avenue", alt: "Historic downtown Burlingame Avenue at dusk" },
  { src: halfMoonBay, city: "Half Moon Bay", caption: "Coastal Escape", alt: "Half Moon Bay California coastline at golden hour" },
  { src: hillsborough, city: "Hillsborough", caption: "Stately Estates", alt: "Hillsborough California traditional luxury mansion" },
  { src: menloPark, city: "Menlo Park", caption: "Santa Cruz Avenue", alt: "Charming downtown Menlo Park California Santa Cruz Avenue" },
  { src: millbrae, city: "Millbrae", caption: "Mediterranean Charm", alt: "Mediterranean luxury home in Millbrae California" },
  { src: redwoodCity, city: "Redwood City", caption: "Marina Sunset", alt: "Redwood City California waterfront marina at sunset" },
  { src: sanBruno, city: "San Bruno", caption: "Hillside Living", alt: "San Bruno California hillside neighborhood with mountain backdrop" },
  { src: sanCarlos, city: "San Carlos", caption: "Laurel Street Nights", alt: "San Carlos California Laurel Street downtown at twilight" },
  { src: sanMateo, city: "San Mateo", caption: "Modern Sanctuary", alt: "Modern luxury San Mateo California home with pool" },
  { src: woodside, city: "Woodside", caption: "Among the Redwoods", alt: "Secluded Woodside California estate among redwood trees" },
];

const PeninsulaCarousel = () => {
  // Duplicate the slides for a seamless infinite loop
  const loop = [...slides, ...slides];

  return (
    <section
      className="py-20 md:py-28 bg-background overflow-hidden"
      aria-label="San Francisco Peninsula lifestyle carousel"
    >
      <div className="container mx-auto max-w-[1400px] px-6 mb-12 md:mb-16 text-center">
        <p className="eyebrow mb-4">The Peninsula</p>
        <h2 className="font-serif text-3xl md:text-5xl font-light text-foreground leading-tight">
          A lifestyle <span className="italic text-primary">worth coming home to.</span>
        </h2>
      </div>

      <div className="relative group">
        {/* Edge gradients for premium fade */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 z-10 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 z-10 bg-gradient-to-l from-background to-transparent" />

        <div
          className="flex w-max gap-4 md:gap-6 animate-marquee group-hover:[animation-play-state:paused] motion-reduce:animate-none"
          style={{ animationDuration: "80s" }}
        >
          {loop.map((slide, i) => (
            <figure
              key={`${slide.city}-${i}`}
              className="relative shrink-0 w-[78vw] sm:w-[420px] md:w-[480px] lg:w-[560px] aspect-[4/3] overflow-hidden bg-secondary"
            >
              <img
                src={slide.src}
                alt={slide.alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-105"
                loading="lazy"
                width={1280}
                height={896}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/70 via-transparent to-transparent" />
              <figcaption className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-brand-ivory">
                <p className="text-[10px] tracking-[0.3em] uppercase text-primary mb-2">
                  {slide.caption}
                </p>
                <p className="font-serif text-2xl md:text-3xl font-light">
                  {slide.city}
                </p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PeninsulaCarousel;
